import { AmountDisplay, amountString } from "@/app/shared/ui/amount-display";
import { useDemoModeStore } from "@/store/demoMode";
import { useAccounts } from "@/hooks/api";
import { accountTypeLabels } from "@/lib/design-tokens";
import { cn, netWorthContribution } from "@/lib/utils";
import { type Account, AccountType } from "@/types";
import { ChevronDown, PanelLeft, PanelLeftClose, Plus, Wallet } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * Minimal Actual Budget–style account picker shown as a second (inner) sidebar
 * on the transactions register pages (/transactions and /accounts/:id).
 * Lists active accounts with balances, an "All Transactions" entry on top and
 * archived accounts behind a collapsible group (like Actual's "Closed").
 */

// Desktop collapse state, persisted across reloads (mirrors the main nav
// sidebar's `groot.sidebar.collapsed` behavior).
const ACCOUNT_SIDEBAR_COLLAPSED_KEY = "nesins.accountSidebar.collapsed";

const ICON_BTN =
  "inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground";

const RAIL_ITEM =
  "flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/60 hover:text-foreground";

const ACCOUNT_TYPE_ORDER: AccountType[] = [
  AccountType.BANK,
  AccountType.CREDIT_CARD,
  AccountType.LOAN,
  AccountType.DIGITAL_WALLET,
  AccountType.CASH,
  AccountType.CRYPTOCURRENCY,
  AccountType.OTHER,
];

interface SidebarItemProps {
  to: string;
  active: boolean;
  name: string;
  balance?: React.ReactNode;
  bold?: boolean;
  icon?: React.ReactNode;
}

function SidebarItem({ to, active, name, balance, bold, icon }: SidebarItemProps) {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-2 mx-2 px-2 py-1.5 rounded-md text-sm",
        active ? "bg-accent font-medium text-foreground" : "text-foreground/80 hover:bg-accent/60",
        bold && "font-medium text-foreground",
      )}
    >
      {icon}
      <span className="truncate flex-1">{name}</span>
      {balance && <span className="text-xs tabular-nums text-muted-foreground">{balance}</span>}
    </Link>
  );
}

function AccountIcon({ account }: { account: Account }) {
  if (account.iconUrl) {
    return (
      <img
        src={account.iconUrl}
        alt=""
        loading="lazy"
        className="size-4 rounded object-contain flex-shrink-0"
      />
    );
  }
  return (
    <span className="size-4 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-[10px] text-muted-foreground">
      {account.accountName.charAt(0).toUpperCase()}
    </span>
  );
}

export function AccountSidebar({
  /** Restrict the picker to one account type (e.g. credit cards hub). */
  filterType,
  allLabel = "All Transactions",
  allTo = "/transactions",
}: {
  filterType?: AccountType;
  allLabel?: string;
  allTo?: string;
}) {
  const { data: allAccounts = [] } = useAccounts();
  const { enabled: isDemoMode } = useDemoModeStore();
  const location = useLocation();
  const [showArchived, setShowArchived] = useState(false);
  // Lazy initializer reads localStorage synchronously to prevent a flash.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(ACCOUNT_SIDEBAR_COLLAPSED_KEY) === "true",
  );

  const setCollapsedPersisted = (next: boolean) => {
    setCollapsed(next);
    localStorage.setItem(ACCOUNT_SIDEBAR_COLLAPSED_KEY, String(next));
  };

  const accounts = filterType ? allAccounts.filter((a) => a.type === filterType) : allAccounts;

  const accountIdMatch = location.pathname.match(/^\/accounts\/(\d+)/);
  const activeAccountId = accountIdMatch ? Number(accountIdMatch[1]) : null;
  const isAllTransactions = location.pathname === allTo;

  const byOrder = (a: Account, b: Account) => {
    const aSortOrder = a.sortOrder ?? null;
    const bSortOrder = b.sortOrder ?? null;

    if (aSortOrder !== null && bSortOrder === null) return -1;
    if (aSortOrder === null && bSortOrder !== null) return 1;
    if (aSortOrder !== null && bSortOrder !== null && aSortOrder !== bSortOrder) {
      return aSortOrder - bSortOrder;
    }
    return a.accountName.localeCompare(b.accountName);
  };
  const activeAccounts = accounts.filter((a) => !a.isArchived && !a.isDeleted).sort(byOrder);
  const archivedAccounts = accounts.filter((a) => a.isArchived && !a.isDeleted).sort(byOrder);

  const groupAccounts = (accountList: Account[]) =>
    ACCOUNT_TYPE_ORDER.map((type) => ({
      type,
      accounts: accountList.filter((account) => account.type === type),
    })).filter((group) => group.accounts.length > 0);

  const renderAccountGroups = (accountList: Account[]) =>
    groupAccounts(accountList).map(({ type, accounts: groupedAccounts }) => (
      <div key={type} className="mt-3 first:mt-0">
        <div className="px-4 pb-1 text-[10px] font-medium uppercase text-muted-foreground/70">
          {accountTypeLabels[type]}
        </div>
        <div className="space-y-0.5">
          {groupedAccounts.map((account) => (
            <SidebarItem
              key={account.id}
              to={`/accounts/${account.id}`}
              active={activeAccountId === account.id}
              name={account.accountName}
              icon={<AccountIcon account={account} />}
              balance={
                <AmountDisplay
                  amount={account.currentBalance || 0}
                  currencyCode={account.currencyCode || "INR"}
                />
              }
            />
          ))}
        </div>
      </div>
    ));

  // Total only makes sense in a single currency
  const currencies = new Set(activeAccounts.map((a) => a.currencyCode || "INR"));
  const singleCurrency = currencies.size === 1 ? [...currencies][0] : null;
  // A mixed list nets out what is owed on cards/loans; a single-type list
  // (e.g. the cards hub) shows its plain total.
  const totalBalance = activeAccounts.reduce(
    (sum, a) => sum + (filterType ? a.currentBalance || 0 : netWorthContribution(a)),
    0,
  );

  if (collapsed) {
    return (
      <aside
        aria-label="Accounts"
        className="hidden md:flex md:flex-col w-12 shrink-0 border-r border-border bg-background md:sticky md:top-0 md:h-screen"
      >
        <div className="flex justify-center px-1 pt-2">
          <button
            type="button"
            onClick={() => setCollapsedPersisted(false)}
            aria-label="Expand accounts sidebar"
            aria-expanded={false}
            className={ICON_BTN}
          >
            <PanelLeft className="size-4" />
          </button>
        </div>

        <div className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-2">
          <Link
            to={allTo}
            title={allLabel}
            aria-label={allLabel}
            className={cn(RAIL_ITEM, isAllTransactions && "bg-accent text-foreground")}
          >
            <Wallet className="size-4" />
          </Link>
          {activeAccounts.length > 0 && (
            <>
              <div className="my-1 h-px w-6 bg-border" aria-hidden="true" />
              {activeAccounts.map((account) => (
                <Link
                  key={account.id}
                  to={`/accounts/${account.id}`}
                  title={`${account.accountName} · ${amountString(account.currentBalance || 0, account.currencyCode || "INR", isDemoMode)}`}
                  aria-label={account.accountName}
                  className={cn(
                    RAIL_ITEM,
                    activeAccountId === account.id && "bg-accent text-foreground",
                  )}
                >
                  <AccountIcon account={account} />
                </Link>
              ))}
            </>
          )}
        </div>

        <div className="flex justify-center border-t border-border py-2">
          <Link
            to="/accounts/new"
            title="New account"
            aria-label="New account"
            className={ICON_BTN}
          >
            <Plus className="size-4" />
          </Link>
        </div>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Accounts"
      className="hidden md:flex md:flex-col w-60 shrink-0 border-r border-border bg-background md:sticky md:top-0 md:h-screen"
    >
      <div className="flex-1 overflow-y-auto py-3">
        <div className="flex justify-end px-2 pb-1">
          <button
            type="button"
            onClick={() => setCollapsedPersisted(true)}
            aria-label="Collapse accounts sidebar"
            aria-expanded
            className={ICON_BTN}
          >
            <PanelLeftClose className="size-4" />
          </button>
        </div>
        <SidebarItem
          to={allTo}
          active={isAllTransactions}
          bold
          name={allLabel}
          icon={<Wallet className="size-4 text-muted-foreground flex-shrink-0" />}
          balance={
            singleCurrency ? (
              <AmountDisplay amount={totalBalance} currencyCode={singleCurrency} />
            ) : undefined
          }
        />

        <div className="px-4 pt-4 pb-1 text-[11px] font-medium uppercase text-muted-foreground">
          Accounts
        </div>
        {activeAccounts.length > 0 ? (
          renderAccountGroups(activeAccounts)
        ) : (
          <p className="px-4 py-1.5 text-xs text-muted-foreground">No accounts yet</p>
        )}

        {archivedAccounts.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="flex items-center gap-1.5 px-4 pt-4 pb-1 w-full text-left text-[11px] font-medium uppercase text-muted-foreground hover:text-foreground"
              aria-expanded={showArchived}
            >
              Archived ({archivedAccounts.length})
              <ChevronDown
                className={cn("size-3 transition-transform", showArchived && "rotate-180")}
              />
            </button>
            {showArchived && renderAccountGroups(archivedAccounts)}
          </>
        )}
      </div>

      <div className="border-t border-border p-2">
        <Link
          to="/accounts/new"
          className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-accent/60 hover:text-foreground"
        >
          <Plus className="size-4" />
          New account
        </Link>
      </div>
    </aside>
  );
}
